import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { action, observable, makeObservable, computed } from 'mobx';
import { UserLoginState } from './UserLoginState';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { PaymentProvidersType } from 'src/AppState/ConfigState/types';
import addError from 'src/utils/error.jsx';
import { TrpcClient } from './TrpcClient';
import {
    updateManualWithdrawal,
    updateManualWithdrawalPaysafe,
    withdrawalListAll,
} from 'src_server/types/manualWithdrawals';
import { apiUpdateWithdrawalRequest } from 'src/api_openapi/v2/yaspa/apiUpdateWithdrawal';
import { Common } from './Common';
import { apiConfirmWithdrawalRequest } from 'src/api_openapi/v2/yaspa/apiConfirmWithdrawal';
import { apiRejectWithdrawalRequest } from 'src/api_openapi/v2/yaspa/apiRejectWithdrawal';
import { YaspaBankingApiRejectPayoutRequestType } from 'src/api_openapi/v2/yaspa/schemas';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Amount } from 'src_common/common/amount/Amount';

export type WithdrawalActionType = 'cancel' | 'reject' | 'complete' | 'assign' | 'unassign';
export type ViewType = 'your' | 'none' | 'other';

export const YASPA_REJECTION_REASON = {
    CANCELLED_BY_STAFF: 'Cancelled by Staff',
    REJECTED_BY_STAFF: 'Rejected by Staff',
} as const satisfies Record<string, YaspaBankingApiRejectPayoutRequestType['rejectionReason']>;

const getBody = (
    staffId: number,
    withdrawalId: number,
    customerId: number,
    action: WithdrawalActionType
): updateManualWithdrawal.TInputSchema => {
    switch (action) {
        case 'complete':
            return {
                action: 'initiate',
                customerId,
                withdrawalId,
                body: {
                    status: 'initiated',
                    assigne: {
                        id: staffId,
                        type: 'staff',
                    },
                },
            };
        case 'cancel':
            return {
                action: 'cancel',
                customerId,
                withdrawalId,
                body: {
                    status: 'cancelled',
                    assigne: {
                        id: staffId,
                        type: 'staff',
                    },
                },
            };
        case 'reject':
            return {
                action: 'reject',
                customerId,
                withdrawalId,
                body: {
                    status: 'rejected',
                    assigne: {
                        id: staffId,
                        type: 'staff',
                    },
                },
            };
        case 'assign':
            return {
                withdrawalId,
                customerId,
                action: 'assign',
                body: {
                    status: 'assigned',
                    reassign: false,
                },
            };
        case 'unassign':
            return {
                withdrawalId,
                customerId,
                action: 'unassign',
                body: {
                    status: 'initiated',
                    reassign: false,
                },
            };
    }
};

const getBodyPaysafe = (
    staffId: number,
    transactionId: number,
    customerId: number,
    action: WithdrawalActionType
): updateManualWithdrawalPaysafe.TInputSchema => {
    const id = staffId.toString();

    switch (action) {
        case 'complete':
            return {
                customerId,
                transactionId,
                body: {
                    status: 'initiated',
                    assignee: {
                        id,
                        type: 'trader',
                        name: 'trader',
                    },
                },
            };
        case 'cancel':
            return {
                customerId,
                transactionId,
                body: {
                    status: 'cancelled',
                    assignee: {
                        id,
                        type: 'trader',
                        name: 'trader',
                    },
                },
            };
        case 'reject':
            return {
                customerId,
                transactionId,
                body: {
                    status: 'rejected',
                    assignee: {
                        id,
                        type: 'trader',
                        name: 'trader',
                    },
                },
            };
        case 'assign':
            return {
                transactionId,
                customerId,
                body: {
                    status: 'assigned',
                    assignee: {
                        id,
                        name: 'trader',
                        type: 'trader',
                    },
                },
            };
        case 'unassign':
            return {
                transactionId,
                customerId,
                body: {
                    status: 'requested',
                    assignee: {
                        id,
                        name: 'trader',
                        type: 'trader',
                    },
                },
            };
    }
};

const getBodyWorldpay = (
    item: WithdrawalModelType
): {
    amount: number;
    currencyCode: string;
    tokenHref: string;
    description: string;
} => ({
    amount: parseFloat(new Amount(String(item.amount)).div(100).value),
    currencyCode: item.currency,
    tokenHref: item.providerReference ?? '',
    description: 'Withdrawal',
});

export class YaspaWithdrawalState {
    public static get = AutoWeakMap.create(
        (common: Common, staffId: number, customerId: number) => new YaspaWithdrawalState(common, staffId, customerId)
    );

    private constructor(
        private readonly common: Common,
        private readonly staffId: number,
        private readonly customerId: number
    ) {}

    private async assignUnassignYaspa(
        transactionId: number,
        action: WithdrawalActionType,
        citizenTransactionId: string
    ): Promise<void> {
        const requestData = getBodyPaysafe(this.staffId, transactionId, this.customerId, action);
        const response = await apiUpdateWithdrawalRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                customerId: requestData.customerId.toString(),
                transactionId: requestData.transactionId,
                requestBody: {
                    citizenTransactionId,
                    status: requestData.body.status,
                    assignee: {
                        ...requestData.body.assignee,
                        id: this.staffId,
                    },
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );

        if (response.status !== 200) {
            addError(response.body.errors?.[0]?.message ?? '');
        }
    }

    private async confirmYaspa(transactionId: number, citizenTransactionId: string): Promise<void> {
        const response = await apiConfirmWithdrawalRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                customerId: this.customerId.toString(),
                requestBody: {
                    transactionId,
                    citizenTransactionId,
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );

        if (response.status !== 200) {
            addError(response.body.errors?.[0]?.message ?? '');
        }
    }

    private async rejectYaspa(
        transactionId: number,
        citizenTransactionId: string,
        rejectionReason: YaspaBankingApiRejectPayoutRequestType['rejectionReason']
    ): Promise<void> {
        const response = await apiRejectWithdrawalRequest(
            this.common.appEnvParams.gatewayUrl,
            {
                customerId: this.customerId.toString(),
                requestBody: {
                    transactionId,
                    citizenTransactionId,
                    rejectionReason,
                },
            },
            {
                authorization: `Bearer ${this.common.session.currentJwt}`,
            }
        );

        if (response.status !== 200) {
            addError(response.body.errors?.[0]?.message ?? '');
        }
    }

    public async execActionYaspa(
        transactionId: number,
        action: WithdrawalActionType,
        providerReference?: string | null
    ): Promise<void> {
        if (providerReference === undefined || providerReference === null) {
            addError('unknown provider reference');
            return;
        }

        if (action === 'complete') {
            await this.confirmYaspa(transactionId, providerReference);
            return;
        }

        if (action === 'assign' || action === 'unassign') {
            await this.assignUnassignYaspa(transactionId, action, providerReference);
            return;
        }

        if (action === 'cancel') {
            await this.rejectYaspa(transactionId, providerReference, YASPA_REJECTION_REASON.CANCELLED_BY_STAFF);
            return;
        }

        await this.rejectYaspa(transactionId, providerReference, YASPA_REJECTION_REASON.REJECTED_BY_STAFF);
    }
}

const convertToId = (item: WithdrawalModelType): number => item.id;
type WithdrawalModelType = withdrawalListAll.WithdrawalViewModelType;

class WithdrawalItem {
    @observable private data: WithdrawalModelType | null;

    public constructor() {
        this.data = null;
        makeObservable(this);
    }

    @action public setData(data: WithdrawalModelType): void {
        this.data = data;
    }

    public getData(): WithdrawalModelType | null {
        return this.data;
    }

    public static create(): WithdrawalItem {
        return new WithdrawalItem();
    }
}

export class WithdrawalState {
    private readonly data: MobxMapAutoNew<number, WithdrawalItem>;
    private readonly list: Resource<Array<number>>;
    @observable private isProcessing: Map<number, boolean>;
    @observable private isCompleting: Set<number>;

    public constructor(
        private readonly trpcClient: TrpcClient,
        public readonly userLoginState: UserLoginState,
        private readonly usePaymentFormForUniverse: PaymentProvidersType,
        private readonly sdkStaff: SdkStaff,
        private readonly common: Common
    ) {
        this.isProcessing = new Map();
        this.isCompleting = new Set();
        this.data = new MobxMapAutoNew(WithdrawalItem.create);

        this.list = new Resource(async (): Promise<Array<number>> => {
            const response = await this.trpcClient.client.manualWithdrawals.withdrawalListAll.query({
                usePaymentFormForUniverse: this.usePaymentFormForUniverse.type,
            });

            if (response.responseStatus === 'error') {
                throw new Error();
            }

            const { data } = response;

            data.sort((a, b) => {
                if (a.tdate > b.tdate) {
                    return 1;
                }
                if (a.tdate < b.tdate) {
                    return -1;
                }
                return 0;
            });

            this.saveList(data);

            return data.map(convertToId);
        });
        makeObservable(this);
    }

    @action private saveList(list: Array<WithdrawalModelType>): void {
        for (const item of list) {
            this.data.get(item.id).setData(item);
        }
    }

    @computed public get allList(): Array<number> | null {
        const result = this.list.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    @computed public get isAnyCompleting(): boolean {
        return this.isCompleting.size > 0;
    }

    public getListByType(type: ViewType): Array<WithdrawalModelType> {
        const all = this.allList;
        const listByType: Array<WithdrawalModelType> = [];
        if (all !== null) {
            for (const id of all) {
                const item = this.getItem(type, id);
                if (item !== null) {
                    listByType.push(item);
                }
            }
        }
        return listByType;
    }

    private getItemInner(id: number): WithdrawalModelType | null {
        return this.data.get(id).getData();
    }

    public getItem(type: ViewType, id: number): WithdrawalModelType | null {
        const item = this.data.get(id).getData();

        if (item === null) {
            return null;
        }

        if (type === 'none') {
            const assigneeId = item.assigneeId ?? null;
            if (assigneeId === null) {
                return item;
            }

            return null;
        }

        const operatorId = this.userLoginState.userId;

        if (operatorId === null) {
            //console.error('The operatorId was expected to be not null');
            return null;
        }

        switch (type) {
            case 'your':
                if (item.assigneeId !== null && item.assigneeId === operatorId) {
                    return item;
                }

                return null;
            case 'other':
                if (item.assigneeId !== null && item.assigneeId !== operatorId) {
                    return item;
                }

                return null;
        }
    }

    @action public async refresh(): Promise<void> {
        console.info('Withdrawal refresh');
        await this.list.refresh();
    }

    public async execActionYaspa(
        transactionId: number,
        action: WithdrawalActionType,
        staffId: number,
        customerId: number,
        providerReference?: string | null
    ): Promise<void> {
        await YaspaWithdrawalState.get(this.common, staffId, customerId).execActionYaspa(
            transactionId,
            action,
            providerReference
        );
    }

    public async execActionRealex(
        withdrawalId: number,
        action: WithdrawalActionType,
        staffId: number,
        customerId: number
    ): Promise<void> {
        const requestData = getBody(staffId, withdrawalId, customerId, action);
        const response = await this.trpcClient.client.manualWithdrawals.updateManualWithdrawal.mutate({
            ...requestData,
        });
        if (response.responseStatus === 'error') {
            addError(response.data.errors?.[0]?.debugDetails ?? '');
        }
    }

    public async execActionPaysafe(
        withdrawalId: number,
        action: WithdrawalActionType,
        staffId: number,
        customerId: number
    ): Promise<void> {
        const requestData = getBodyPaysafe(staffId, withdrawalId, customerId, action);
        const response = await this.trpcClient.client.manualWithdrawals.updateManualWithdrawalPaysafe.mutate({
            ...requestData,
        });
        if (response.responseStatus === 'error') {
            addError(response.data.errors?.[0]?.debugDetails ?? '');
        }
    }

    public async execActionWorldpay(
        withdrawalId: number,
        action: WithdrawalActionType,
        staffId: number,
        customerId: number
    ): Promise<void> {
        const item = this.getItemInner(withdrawalId);

        if (item === null) {
            addError(`Expected withdrawal for id=${withdrawalId}`);
            return;
        }

        if (action === 'complete') {
            const response = await this.trpcClient.client.worldpayRouter.withdrawalComplete.mutate({
                customerId,
                withdrawalId,
                body: getBodyWorldpay(item),
            });

            if (response.responseStatus === 'error') {
                addError(response.data.errors?.[0]?.message ?? 'Unknown error occurred');
            }
        } else {
            const requestData = getBody(staffId, withdrawalId, customerId, action);
            const response = await this.trpcClient.client.worldpayRouter.withdrawalAction.mutate({
                ...requestData,
            });
            if (response.responseStatus === 'error') {
                addError(response.data.errors?.[0]?.debugDetails ?? '');
            }
        }
    }

    @action public async execAction(withdrawalId: number, action: WithdrawalActionType): Promise<string | void> {
        const flag = this.isProcessing.get(withdrawalId) ?? false;
        if (flag) {
            console.error('Duplicate request', withdrawalId, action);
            return;
        }
        this.isProcessing.set(withdrawalId, true);

        if (action === 'complete') {
            this.isCompleting.add(withdrawalId);
        }

        try {
            await this.execActionPrivate(withdrawalId, action);
        } catch (error) {
            console.error(error);
        }

        this.isProcessing.delete(withdrawalId);
        this.isCompleting.delete(withdrawalId);
    }

    private async execActionPrivate(withdrawalId: number, action: WithdrawalActionType): Promise<string | void> {
        const staffId = this.sdkStaff.getStaffId();
        const item = this.getItemInner(withdrawalId);

        if (item === null || staffId === null) {
            console.error(`Expected withdrawal for id=${withdrawalId}`);
            return;
        }

        try {
            switch (item.provider) {
                case 'paysafe':
                    await this.execActionPaysafe(withdrawalId, action, staffId, item.accountId);
                    break;
                case 'realex':
                    await this.execActionRealex(withdrawalId, action, staffId, item.accountId);
                    break;
                case 'worldpay':
                    await this.execActionWorldpay(withdrawalId, action, staffId, item.accountId);
                    break;
                case 'yaspa':
                    await this.execActionYaspa(withdrawalId, action, staffId, item.accountId, item.providerReference);
                    break;
            }
        } catch {
            addError('Failed to resolve withdrawal, check network response');
        }

        await this.refresh();
    }
}
